import React, { useEffect } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { FloatingCalculadoraBCV } from "./components/common/CalculadoraBCV/FloatingCalculadoraBCV";
import { PageNotFound } from "./components/views/PageNotFound/PageNotFound";
import { ProtectedRoute } from "./components/common/ProtectedRoute/ProtectedRoute";
import { Login } from "./components/views/Login/Login";
import auth from "./services/authService";
import {
  ExternalProviders,
  InternalProviders,
  Fleet,
} from "./components/views/Providers/Providers";
import { Scheduled } from "./components/views/Stats/Scheduled/Scheduled.jsx";
import { DriversLocation } from "./components/views/Providers/DriversLocation/DriversLocation.jsx";
import {
  NewProvider,
  NewInternalProvider,
  NewVehicularAsset,
} from "./components/views/Providers/NewProvider/NewProvider";
import { FollowingLink } from "./components/views/FollowingLink/FollowingLink";
import { FollowinLinks } from "./components/views/Providers/FollowinLinks/FollowinLinks";
import { MessageFollowing } from "./components/views/FollowingLink/MessageFollowing.jsx";
import {
  EditExternalProvider,
  EditInternalProvider,
  EditVehicularAsset,
} from "./components/views/Providers/EditProvider/EditProvider";
import { Services } from "./components/views/Services/Services";
import { Stats } from "./components/views/Services/Stats";
import { Exportables } from "./components/views/Stats/Exportables/Exportables";
import { ProvidersPayment } from "./components/views/Providers/ProvidersPayment/ProvidersPayment.jsx";
import { ParticularServicesCounter } from "./components/views/Providers/ParticularServicesCounter/ParticularServicesCounter.jsx";
import { OperatorCommissions } from "./components/views/Providers/OperatorCommissions/OperatorCommissions.jsx";
import { Operations } from "./components/views/Stats/Operations/Operations";
import { Reviews } from "./components/views/Reviews/Reviews";
import { ParticularIncidence } from "./components/views/Incidence/ParticularIncidence";
import { AssingIncidence } from "./components/views/Incidence/AssingIncidence";
import { AffiliatedIncidence } from "./components/views/Incidence/AffiliatedIncidence";
import { ExpressCheck } from "./components/views/ExpressCheck/ExpressCheck";
import { IncidenceDetail } from "./components/views/Incidence/IncidenceDetail";
import { EditCase } from "./components/views/Incidence/EditCase";
import { Alliances } from "./components/views/Alliances/Alliances";
import { OperationsUsers } from "./components/views/OperationsUsers/OperationsUsers";
import { Parameters } from "./components/views/Configurations/Parameters/Parameters";
import { Garage } from "./components/views/Garage/Garage";
import "react-toastify/dist/ReactToastify.css";
import { operationsRoles } from "./assets/helpers/roles";
import "./App.scss";
import FleetMaintenance from "./components/views/FleetMaintenance/FleetMaintenance.jsx";
import ApproveAVFaultsResolved from "./components/views/ApproveAVFaultsResolved/ApproveAVFaultsResolved.jsx";
import { Complaints } from "./components/views/Complaints/Complaints.jsx";
import { BatterySales } from "./components/views/BatterySales/BatterySales.jsx";
import { TvSection } from "./components/views/Stats/TvSection/TvSection";
import RideryCodes from "./components/views/RideryCodes/RideryCodes.jsx";
import { PaymentsValidation } from "./components/views/Admin/PaymentsValidation/PaymentsValidation.jsx";
import { ParticularPotentialClients } from "./components/views/PotentialClients/ParticularPotentialClients";
import { EnvBanner } from "./components/common/EnvBanner/EnvBanner";

function App() {
  const {
    operationsAdmin,
    operationsDev,
    operationsSupervisor,
    operationsOperator,
    OPERATIONS_FLOTA_AV,
    OPERATIONS_FLOTA_GRUA,
    OPERATIONS_FLOTA_GRUA_EXT,
  } = operationsRoles;

  // La calculadora solo se muestra si el usuario tiene una sesion activa.
  const isLoggedIn = !!auth.getToken();
  const location = useLocation();
  const PUBLIC_PATHS = ["/login", '/cashea-confirmation'];
  const isPublicView =
    location.pathname === "/" ||
    PUBLIC_PATHS.some((path) => location.pathname.startsWith(path));

  useEffect(() => {
    const currentUrl = new URL(window.location.href);

    if (!currentUrl.searchParams.has("_gl")) {
      return;
    }

    // Keep linker parameter briefly so GTM/GA can consume attribution data.
    const timer = setTimeout(() => {
      currentUrl.searchParams.delete("_gl");
      const search = currentUrl.searchParams.toString();
      const cleanUrl = `${currentUrl.pathname}${search ? `?${search}` : ""}${currentUrl.hash}`;
      window.history.replaceState({}, "", cleanUrl);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <React.Fragment>
      <EnvBanner />
      <div className="app-content">
        <div>
          <Switch>
            <Route
              path="/login"
              render={(props) => {
                return <Login {...props} props={props} />;
              }}
            />
            <Route
              path="/following-link/:caseId/:status"
              render={(props) => {
                return <MessageFollowing {...props} props={props} />;
              }}
            />
            <Route
              path="/following-link/:caseId"
              render={(props) => {
                return <FollowingLink {...props} props={props} />;
              }}
            />
            <ProtectedRoute
              path="/services"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
                // OPERATIONS_FLOTA_AV,
                // OPERATIONS_FLOTA_GRUA,
                // OPERATIONS_FLOTA_GRUA_EXT,
              ]} // Tipos de usuario autorizados a esta ruta
              component={Services}
            />
            <ProtectedRoute
              path="/providers-stats"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
                OPERATIONS_FLOTA_AV,
                OPERATIONS_FLOTA_GRUA,
                OPERATIONS_FLOTA_GRUA_EXT,
              ]} // Tipos de usuario autorizados a esta ruta
              component={Stats}
            />
            {/* Estadisticas */}
            <ProtectedRoute
              path="/stats/operations"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={Operations}
            />
            <ProtectedRoute
              path="/stats/exportable"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={Exportables}
            />
            <ProtectedRoute
              path="/stats/reviews"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={Reviews}
            />
            <ProtectedRoute
              path="/stats/tv-section"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={TvSection}
            />
            <ProtectedRoute
              path="/stats/complaints"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={Complaints}
            />
            {/* Clientes Potenciales */}
            <ProtectedRoute
              path="/stats/particular-clients"
              level={[operationsDev, operationsAdmin, operationsSupervisor, operationsOperator]}
              component={ParticularPotentialClients}
            />
            {/* Incidence */}
            <ProtectedRoute
              path="/incidence/assign-provider/:caseId/service/:serviceId"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={AssingIncidence}
            />
            <ProtectedRoute
              path="/incidence/detail/:caseId"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={IncidenceDetail}
            />
            <ProtectedRoute
              path="/incidence/edit/:caseId"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={EditCase}
            />
            <ProtectedRoute
              path="/incidence/create-affiliated"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={AffiliatedIncidence}
            />
            <ProtectedRoute
              path="/incidence/create/:type"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={ParticularIncidence}
            />
            {/* Proveedores de Gruas */}
            <ProtectedRoute
              path="/providers/external/create"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={NewProvider}
            />
            <ProtectedRoute
              path="/providers/alliances"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={Alliances}
            />
            <ProtectedRoute
              path="/providers/providers-payments"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={ProvidersPayment}
            />
            <ProtectedRoute
              path="/providers/battery-sales"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={BatterySales}
            />
            <ProtectedRoute
              path="/providers/approve-faults-resolved"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={ApproveAVFaultsResolved}
            />
            <ProtectedRoute
              path="/stats/operators-counter/commissions"
              level={[
                operationsDev,
                operationsAdmin,
                operationsSupervisor,
              ]}
              component={OperatorCommissions}
            />
            <ProtectedRoute
              path={"/stats/operators-counter"}
              level={[
                operationsDev,
                operationsAdmin,
                operationsOperator,
                operationsSupervisor,
              ]} // Tipos de usuario autorizados a esta ruta
              component={ParticularServicesCounter}
            />
            <ProtectedRoute
              path="/stats/scheduled-service"
              level={[
                operationsDev,
                operationsAdmin,
                operationsOperator,
                operationsSupervisor,
              ]} // Tipos de usuario autorizados a esta ruta
              component={Scheduled}
            />
            <ProtectedRoute
              path="/providers/external/edit/:providerId"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={EditExternalProvider}
            />
            <ProtectedRoute
              path="/providers/external"
              level={[
                operationsDev,
                operationsAdmin,
                operationsOperator,
                operationsSupervisor,
              ]} // Tipos de usuario autorizados a esta ruta
              component={ExternalProviders}
            />
            <ProtectedRoute
              path="/incidence/express-check/:caseId"
              level={[
                operationsDev,
                operationsAdmin,
                OPERATIONS_FLOTA_GRUA,
                OPERATIONS_FLOTA_AV,
              ]} // Tipos de usuario autorizados a esta ruta
              component={ExpressCheck}
            />
            {/* Proveedores de Motos */}
            {/* Flota interna */}
            <ProtectedRoute
              path="/fleet/internal/create"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={NewInternalProvider}
            />
            <ProtectedRoute
              path="/fleet/internal/edit/:providerId"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={EditInternalProvider}
            />
            <ProtectedRoute
              path="/fleet/internal"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={InternalProviders}
            />
            <ProtectedRoute
              path="/fleet/create"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={NewVehicularAsset}
            />
            <ProtectedRoute
              path="/fleet/edit/:vehicleId"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={EditVehicularAsset}
            />
            <ProtectedRoute
              path="/fleet/maintenance"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={FleetMaintenance}
            />
            {/* <ProtectedRoute
              path="/fleet/fleet-location"
              level={[
                operationsAdmin,
                operationsDev,
                operationsSupervisor,
                operationsOperator,
                OPERATIONS_FLOTA_AV,
                OPERATIONS_FLOTA_GRUA,
              ]} // Tipos de usuario autorizados a esta ruta
              component={FleetLocation}
            /> */}
            <ProtectedRoute
              path="/fleet/drivers-location"
              level={[
                operationsAdmin,
                operationsDev,
                operationsSupervisor,
                operationsOperator,
              ]} // Tipos de usuario autorizados a esta ruta
              component={DriversLocation}
            />
            <ProtectedRoute
              path="/fleet/folowing-links"
              level={[
                operationsDev,
                operationsAdmin,
                operationsOperator,
                operationsSupervisor,
              ]} // Tipos de usuario autorizados a esta ruta
              component={FollowinLinks}
            />
            <ProtectedRoute
              path="/fleet"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={Fleet}
            />
            <ProtectedRoute
              path="/garage/billing"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={Garage}
            />
            {/* Administración */}
            <ProtectedRoute
              path="/admin/payments-validation"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={PaymentsValidation}
            />
            {/* Configurations */}
            <ProtectedRoute
              path="/config/users"
              level={[operationsDev, operationsAdmin, operationsSupervisor]} // Tipos de usuario autorizados a esta ruta
              component={OperationsUsers}
            />
            <ProtectedRoute
              path="/config/ridery-codes"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={RideryCodes}
            />
            <ProtectedRoute
              path="/config/parameters"
              level={[operationsDev, operationsAdmin]} // Tipos de usuario autorizados a esta ruta
              component={Parameters}
            />
            <Route
              path="/not-found"
              render={(props) => {
                return <PageNotFound {...props} props={props} />;
              }}
            />
            <Route
              path="/"
              exact
              render={(props) => {
                return (
                  <ProtectedRoute
                    path="/"
                    level={["ADMIN"]} // Tipos de usuario autorizados a esta ruta
                    component={Login}
                  />
                );
              }}
            />
            <Route component={PageNotFound} />
          </Switch>
        </div>
        {(!isPublicView && isLoggedIn) && <FloatingCalculadoraBCV />}
      </div>
    </React.Fragment>
  );
}

export default App;
